/**
 * Paket bearbeiten — eigene, ganze Seite `/katalog/pakete/:id` (WARTESCHLANGE Punkt 129, Antoine
 * 30.09.2026: „Das ist zu schmal, das muss eine ganze Seite sein, um die Pakete zu bearbeiten.“).
 * Bis dahin lag der Editor in einem 440-px-Drawer auf `pages/pakete.tsx`, Positionsnamen wurden
 * abgeschnitten. Funktionen unverändert übernommen: Preis/Name/Beschreibung (Autosave per Blur),
 * Menüs (aktiv schalten, umbenennen, löschen, neu), Positionen (Katalog-Suche oder Freitext,
 * Menge/Gast, Preis-Überschreibung, Warnung bei archivierter Position) — plus „Duplizieren“.
 *
 * Datenweg wie zuvor: GET /api/packages/:id(?menuId=), PATCH /api/packages/:id, Menüs unter
 * /api/packages/:id/menus, PUT /api/packages/:id/items?menuId= (CAS über itemsVersion, M-49).
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { CatalogItem } from "../ui/types";
import { useContentRefetch } from "../ui/CopilotProvider";
import { paketPreisart } from "@maestro/pricing";
import { PaketWebsiteKarte, PaketWebsiteVorschau, websiteLuecken, type WebsiteKategorie } from "../ui/PaketWebsiteKarte";
import { centsToEuros, money, parseGermanDecimal, parseGermanEurosToCents } from "../ui/format";
import { Button, Card, ErrorNote, Icon, Input, LoadingRows, Modal, PageHeader, Select, Textarea, useBestaetigung } from "../ui/components";
import { AllergenBadges } from "../ui/lmiv";
import { PaketAufteilungFelder } from "../ui/PaketAufteilungFelder";
import { MENUE_ART_OPTIONEN, PaketMenuewahlKarte } from "../ui/PaketMenuewahlKarte";
import type { Pkg } from "./pakete";

/** Migration 390: ein Menue INNERHALB eines Pakets - genau eines je Paket ist aktiv. */
interface PkgMenu {
  id: string;
  name: string;
  active: boolean;
  sortOrder: number;
  /** sql/771 (Menüwahl im Paket): Art und Aufpreis je Gast. */
  art?: string | null;
  aufpreisPpCents?: number;
}
interface PkgItem {
  id: string;
  catalogItemId: string | null;
  packageMenuId?: string | null;
  qtyPerGuestMilli: number;
  courseLabel: string | null;
  name: string | null;
  priceCents: number | null;
  /** Migration 390: gesetzt, wenn Name/Preis NUR fuer diese Paket-Position ueberschrieben sind. */
  nameOverride?: string | null;
  priceCentsOverride?: number | null;
  /** "catalog" = aus dem Katalog uebernommen, "freetext" = frei benannte Position ohne Katalog-Bezug. */
  source?: "catalog" | "freetext";
  // LMIV je Gang (Tri-State: null = ungepflegt, [] = geprüft keine, Codes = geprüft).
  allergens?: string[] | null;
  // H-02-Rest (Migration 336): nur bei allergens=[] relevant, siehe ui/lmiv.tsx.
  allergensCheckedAt?: string | null;
  dietary?: Record<string, boolean>;
  // P2: Gang wurde archiviert (z. B. Karte ersetzt) — Warn-Kennzeichnung.
  itemArchivedAt?: string | null;
}
type Detail = { package: Pkg; menus: PkgMenu[]; selectedMenuId: string | null; items: PkgItem[] };

/** Gleiche Grenze wie der Server (`apps/api/src/routes/packages.ts`, PAKET_POSITION_NAME_MAX). */
const PAKET_POSITION_NAME_MAX = 1000;

/** Katalog-Item, das per Klick uebernommen wird - Name+Preis kommen 1:1 aus dem Katalog. */
function suggestionsFor(query: string, catalog: CatalogItem[]): CatalogItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return catalog.filter((ci) => ci.name.toLowerCase().includes(q)).slice(0, 8);
}

/** Textfeld auf die Hoehe seines Inhalts bringen (field-sizing: content kann Safari noch nicht). */
function passeHoeheAn(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight + 2}px`;
}

const sectionTitle = "font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim";

export default function PaketBearbeitenPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  // Design-Durchsicht E12: Menü umbenennen im MAESTRO-Dialog statt nativem `prompt`.
  const [umbenennen, setUmbenennen] = useState<{ id: string; name: string } | null>(null);
  // GESAMTER Katalog (Speisen UND Getränke, alle Speisekarten) - die Positionssuche filtert nur
  // clientseitig ueber genau diese Liste.
  const catalogQ = useApi<CatalogItem[]>("/api/catalog/items");
  // Migration 391: Positionen einer deaktivierten Karte werden im Autovorschlag nicht angeboten.
  const cardsForFilterQ = useApi<{ id: string; active: boolean }[]>("/api/menu-cards");
  const catsForFilterQ = useApi<{ id: string; cardId?: string | null }[]>("/api/catalog/categories");
  const cardActiveById = useMemo(() => new Map((cardsForFilterQ.data ?? []).map((k) => [k.id, k.active])), [cardsForFilterQ.data]);
  const cardIdByCategory = useMemo(() => new Map((catsForFilterQ.data ?? []).map((c) => [c.id, c.cardId ?? null])), [catsForFilterQ.data]);
  const catalog = useMemo(
    () =>
      (catalogQ.data ?? []).filter((ci) => {
        const cardId = ci.categoryId ? (cardIdByCategory.get(ci.categoryId) ?? null) : null;
        if (cardId == null) return true;
        return cardActiveById.get(cardId) !== false;
      }),
    [catalogQ.data, cardIdByCategory, cardActiveById],
  );

  const [pkg, setPkg] = useState<Pkg | null>(null);
  // Pakete-Widget P1: Website-Kategorien fuer die Karte „Auf der Website“.
  const kategorienQ = useApi<WebsiteKategorie[]>("/api/package-categories");
  const kategorien = kategorienQ.data ?? [];
  // Migration 750: andere, nicht archivierte Pakete als Wahl fuer „Weinbegleitung (Paket)“.
  const paketeQ = useApi<Pkg[]>("/api/packages");
  const weinKandidaten = (Array.isArray(paketeQ.data) ? paketeQ.data : [])
    .filter((p) => p.id !== id && p.archivedAt == null)
    .map((p) => ({ id: p.id, name: p.name }));
  // 409 price_jump_confirm: Preisaenderung > 30 % an einem sichtbaren Paket wartet auf Bestaetigung.
  const [preisRueckfrage, setPreisRueckfrage] = useState<{ patch: Partial<Pkg>; vonCents: number; aufCents: number; prozent: number } | null>(null);
  const preisFeldRef = useRef<HTMLInputElement>(null);
  const [menus, setMenus] = useState<PkgMenu[]>([]);
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);
  const [items, setItems] = useState<PkgItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Punkt R: live getippter Paketpreis (Cent) — die Aufteilung rechnet den Rest sofort mit.
  const [preisLive, setPreisLive] = useState<number | null>(null);
  const [preisGeaendert, setPreisGeaendert] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // Menüwahl: ist „Gast wählt“ an, braucht ein neues Menü gleich seine Art (Pflicht) und optional den Aufpreis.
  const [neuMenueArt, setNeuMenueArt] = useState("");
  const [neuMenueAufpreis, setNeuMenueAufpreis] = useState("");
  // sql/771: ruhige Meldung des Servers zur Menüwahl (422), statt des roten Fehlerkastens.
  const [menuewahlMeldung, setMenuewahlMeldung] = useState<string | null>(null);
  const [addPosOpen, setAddPosOpen] = useState(false);
  const [posName, setPosName] = useState("");
  const [posPrice, setPosPrice] = useState("");
  const [posMatchId, setPosMatchId] = useState<string | null>(null);
  const [posSuggestOpen, setPosSuggestOpen] = useState(false);
  const suggestions = useMemo(() => suggestionsFor(posName, catalog), [posName, catalog]);

  // M-47: ein Ladefehler sperrt das Editieren - sonst schickte das naechste Hinzufuegen per
  // Complete-List-PUT NUR die neue Position und loeschte die tatsaechlich vorhandenen.
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [itemsLoadError, setItemsLoadError] = useState<string | null>(null);
  // M-49: Versionszaehler der Positionsliste (CAS gegen Mehrtab/Out-of-order) als Ref.
  const itemsVersionRef = useRef(0);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  function loadPackage(menuId?: string) {
    setItemsLoaded(false);
    setItemsLoadError(null);
    const qs = menuId ? `?menuId=${encodeURIComponent(menuId)}` : "";
    void apiMutate<Detail>(`/api/packages/${id}${qs}`, "GET")
      .then((d) => {
        setPkg(d.package);
        setMenus(d.menus ?? []);
        setSelectedMenuId(d.selectedMenuId);
        setItems(d.items);
        itemsVersionRef.current = d.package.itemsVersion ?? 0;
        setItemsLoaded(true);
      })
      .catch((e) => setItemsLoadError(e instanceof Error ? e.message : String(e)));
  }
  useEffect(() => {
    setPkg(null);
    setItems([]);
    setMenus([]);
    setSelectedMenuId(null);
    setFormError(null);
    loadPackage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // Review-Befund 31.07.2026: Änderungen aus dem Assistenten sofort zeigen.
  useContentRefetch("package", () => loadPackage(selectedMenuId ?? undefined), id);

  function fehler(e: unknown) {
    setFormError(e instanceof Error ? e.message : String(e));
  }

  async function patchPkg(patch: Partial<Pkg> & { confirmPriceChange?: boolean }) {
    // Pakete-Widget P1: Entsteht durch die Aenderung eine Luecke (Uebersetzung geleert, letzte
    // Kategorie abgewaehlt), nimmt die Seite das Paket von der Website, statt am 422 zu scheitern.
    // Nur mit geladenen Kategorien - sonst saehe jedes sichtbare Paket wie „ohne Kategorie“ aus.
    if (pkg?.websiteVisible && patch.websiteVisible === undefined && kategorienQ.data) {
      const aktiveIds = new Set(kategorien.filter((k) => k.archivedAt == null).map((k) => k.id));
      if (websiteLuecken({ ...pkg, ...patch }, aktiveIds)) patch = { ...patch, websiteVisible: false };
    }
    // M-O: Fehler nicht verschlucken — sonst wirkt ein fehlgeschlagenes PATCH wie „gespeichert".
    try {
      await apiMutate(`/api/packages/${id}`, "PATCH", patch);
      const { confirmPriceChange: _bestaetigt, ...gespeichert } = patch;
      setPkg((p) => (p ? { ...p, ...gespeichert } : p));
      setFormError(null);
    } catch (e) {
      const err = e as { code?: string; body?: { vonCents?: number; aufCents?: number; prozent?: number } };
      if (err.code === "price_jump_confirm" && err.body) {
        setPreisRueckfrage({ patch, vonCents: err.body.vonCents ?? 0, aufCents: err.body.aufCents ?? 0, prozent: err.body.prozent ?? 0 });
        return;
      }
      fehler(e);
    }
  }

  /** sql/771: Art/Aufpreis eines Menüs speichern; 422-Meldungen zur Menüwahl erscheinen ruhig an der Karte. */
  async function patchMenue(menuId: string, patch: { art?: string | null; aufpreisPpCents?: number }) {
    try {
      await apiMutate(`/api/packages/${id}/menus/${menuId}`, "PATCH", patch);
      setMenus((prev) => prev.map((m) => (m.id === menuId ? { ...m, ...patch } : m)));
      setMenuewahlMeldung(null);
    } catch (e) {
      const err = e as { code?: string };
      if (err.code?.startsWith("menuewahl_")) setMenuewahlMeldung(e instanceof Error ? e.message : String(e));
      else fehler(e);
    }
  }
  async function schalteMenuewahl(an: boolean) {
    try {
      await apiMutate(`/api/packages/${id}`, "PATCH", { gastWaehltMenue: an });
      setPkg((p) => (p ? { ...p, gastWaehltMenue: an } : p));
      setMenuewahlMeldung(null);
    } catch (e) {
      const err = e as { code?: string };
      if (err.code?.startsWith("menuewahl_")) setMenuewahlMeldung(e instanceof Error ? e.message : String(e));
      else fehler(e);
    }
  }

  async function duplicatePkg() {
    setBusy(true);
    try {
      const copy = await apiMutate<{ id: string }>(`/api/packages/${id}/duplicate`, "POST", {});
      navigate(`/katalog/pakete/${copy.id}`);
    } catch (e) {
      fehler(e);
    } finally {
      setBusy(false);
    }
  }

  function selectMenu(menuId: string) {
    if (menuId === selectedMenuId) return;
    loadPackage(menuId);
  }

  async function addMenu(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const name = ((new FormData(e.currentTarget).get("menuName") as string) ?? "").trim();
    if (!name) return;
    const koerper: { name: string; art?: string; aufpreisPpCents?: number } = { name };
    if (pkg?.gastWaehltMenue === true) {
      // Der Server lehnt ein Menü ohne Art bei eingeschalteter Menüwahl ab (422 menuewahl_art_fehlt): gleich hier sagen.
      if (!neuMenueArt) {
        setMenuewahlMeldung("Für die Menüwahl braucht jedes Menü eine Art. Bitte die Art wählen.");
        return;
      }
      const cents = neuMenueAufpreis.trim() === "" ? 0 : parseGermanEurosToCents(neuMenueAufpreis);
      if (cents == null || cents < 0) {
        setMenuewahlMeldung("Bitte den Aufpreis als Betrag in Euro angeben, zum Beispiel 5,00.");
        return;
      }
      koerper.art = neuMenueArt;
      koerper.aufpreisPpCents = cents;
    }
    try {
      const menu = await apiMutate<PkgMenu>(`/api/packages/${id}/menus`, "POST", koerper);
      setNeuMenueArt("");
      setNeuMenueAufpreis("");
      setMenuewahlMeldung(null);
      setAddMenuOpen(false);
      loadPackage(menu.id);
    } catch (e) {
      fehler(e);
    }
  }

  async function activateMenu(menuId: string) {
    try {
      await apiMutate(`/api/packages/${id}/menus/${menuId}`, "PATCH", { active: true });
      loadPackage(menuId);
    } catch (e) {
      fehler(e);
    }
  }

  async function renameMenu(menuId: string, name: string) {
    if (!name.trim()) return;
    try {
      await apiMutate(`/api/packages/${id}/menus/${menuId}`, "PATCH", { name: name.trim() });
      setMenus((prev) => prev.map((m) => (m.id === menuId ? { ...m, name: name.trim() } : m)));
    } catch (e) {
      fehler(e);
    }
  }

  // Antoine, 10.09.2026: "Es müssen auch Varianten [Menüs] gelöscht werden können." Die Route
  // schuetzt das letzte und das aktive Menue.
  async function deleteMenu(menuId: string, name: string) {
    if (
      !(await bestaetigen({
        titel: `Menü „${name}“ löschen?`,
        text: "Die enthaltenen Positionen gehen dabei verloren.",
        bestaetigen: "Löschen",
        gefahr: true,
      }))
    )
      return;
    try {
      await apiMutate(`/api/packages/${id}/menus/${menuId}`, "DELETE");
      setMenus((prev) => prev.filter((m) => m.id !== menuId));
      if (selectedMenuId === menuId) {
        const naechste = menus.find((m) => m.id !== menuId);
        if (naechste) loadPackage(naechste.id);
      }
    } catch (e) {
      fehler(e);
    }
  }

  async function saveItems(next: PkgItem[]) {
    if (!itemsLoaded || !selectedMenuId) return; // M-47: nie auf Basis eines nicht geladenen Stands schreiben
    const prev = items;
    const menuId = selectedMenuId;
    setItems(next); // optimistisch
    // M-49: Saves serialisieren - schnelle Folge-Klicks duerfen sich nicht gegenseitig ueberholen.
    saveChainRef.current = saveChainRef.current.then(async () => {
      try {
        const res = await apiMutate<{ count: number; itemsVersion: number }>(`/api/packages/${id}/items?menuId=${encodeURIComponent(menuId)}`, "PUT", {
          items: next.map((it) => ({
            catalogItemId: it.catalogItemId,
            nameOverride: it.nameOverride ?? null,
            priceCentsOverride: it.priceCentsOverride ?? null,
            qtyPerGuestMilli: it.qtyPerGuestMilli,
            courseLabel: it.courseLabel,
          })),
          itemsVersion: itemsVersionRef.current,
        });
        itemsVersionRef.current = res.itemsVersion;
      } catch (e) {
        const conflict = (e as { statusCode?: number }).statusCode === 409;
        setFormError(
          conflict
            ? "Die Positionen wurden zwischenzeitlich geändert (anderer Tab?) – Stand neu geladen, bitte Änderung wiederholen."
            : e instanceof Error
              ? e.message
              : String(e),
        );
        // M-O: nach Fehler den Server-Stand (inkl. Version) neu laden; Rollback als Fallback.
        void apiMutate<Detail>(`/api/packages/${id}?menuId=${encodeURIComponent(menuId)}`, "GET")
          .then((d) => {
            setItems(d.items);
            itemsVersionRef.current = d.package.itemsVersion ?? 0;
          })
          .catch(() => setItems(prev));
      }
    });
    await saveChainRef.current;
  }

  function pickSuggestion(ci: CatalogItem) {
    setPosName(ci.name);
    setPosPrice(centsToEuros(ci.priceCents));
    setPosMatchId(ci.id);
    setPosSuggestOpen(false);
  }

  /**
   * Ein Feld fuer beides: Katalog-Treffer werden beim Tippen live vorgeschlagen; ein Klick
   * uebernimmt Name+Preis (catalogItemId). Kein Treffer -> frei benannte Position mit eigenem Preis.
   */
  async function submitAddPosition(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!itemsLoaded) return;
    const trimmedName = posName.trim();
    if (!trimmedName) {
      setFormError("Bitte einen Namen für die Position eingeben.");
      return;
    }
    const matched = posMatchId ? catalog.find((ci) => ci.id === posMatchId && ci.name === trimmedName) : null;
    if (matched) {
      if (items.some((i) => i.catalogItemId === matched.id)) {
        setFormError("Diese Katalog-Position ist bereits in der Menue enthalten.");
        return;
      }
      void saveItems([
        ...items,
        {
          id: crypto.randomUUID(),
          catalogItemId: matched.id,
          qtyPerGuestMilli: 1000,
          courseLabel: null,
          name: matched.name,
          priceCents: matched.priceCents,
          allergens: matched.allergens,
          allergensCheckedAt: matched.allergensCheckedAt,
          dietary: matched.dietary,
          source: "catalog",
        },
      ]);
    } else {
      const priceCents = parseGermanEurosToCents(posPrice);
      if (priceCents == null) {
        setFormError("Kein Katalog-Treffer für diesen Namen – bitte einen eigenen Preis eingeben, z. B. 6,50.");
        return;
      }
      void saveItems([
        ...items,
        {
          id: crypto.randomUUID(),
          catalogItemId: null,
          qtyPerGuestMilli: 1000,
          courseLabel: null,
          name: trimmedName,
          priceCents,
          nameOverride: trimmedName,
          priceCentsOverride: priceCents,
          source: "freetext",
        },
      ]);
    }
    setFormError(null);
    setPosName("");
    setPosPrice("");
    setPosMatchId(null);
    setAddPosOpen(false);
  }

  /** Positionsname umbenennen - bei einer Katalog-Position eine Ueberschreibung NUR fuer dieses Paket. */
  function renameItem(it: PkgItem, name: string) {
    const trimmed = name.trim();
    if (!trimmed || trimmed === (it.name ?? "")) return;
    void saveItems(items.map((x) => (x.id === it.id ? { ...x, name: trimmed, nameOverride: trimmed } : x)));
  }
  /** Preis ueberschreiben - bei Katalog-Positionen eine Ueberschreibung NUR fuer dieses Paket. */
  function repriceItem(it: PkgItem, value: string) {
    const cents = parseGermanEurosToCents(value);
    if (cents == null) {
      setFormError("Ungültiger Preis – z. B. 6,50.");
      return;
    }
    if (cents === it.priceCents) return;
    void saveItems(items.map((x) => (x.id === it.id ? { ...x, priceCents: cents, priceCentsOverride: cents } : x)));
  }

  const zurueck = { label: "Pakete", to: "/katalog/pakete" };

  if (!pkg) {
    return (
      <>
        <PageHeader title="Paket" zurueck={zurueck} />
        {itemsLoadError ? (
          <Card className="overflow-hidden">
            <ErrorNote zentriert title="Paket konnte nicht geladen werden" message={itemsLoadError} onRetry={() => loadPackage()} />
          </Card>
        ) : (
          <LoadingRows />
        )}
      </>
    );
  }

  const art = paketPreisart(pkg);
  return (
    <div key={pkg.id}>
      {bestaetigungsDialog}
      <Modal
        open={umbenennen != null}
        onClose={() => setUmbenennen(null)}
        width="440px"
        title="Menü umbenennen"
        onSubmit={(e) => {
          e.preventDefault();
          if (umbenennen?.name.trim()) void renameMenu(umbenennen.id, umbenennen.name);
          setUmbenennen(null);
        }}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setUmbenennen(null)}>Abbrechen</Button>
            <Button type="submit" variant="primary" disabled={!umbenennen?.name.trim()}>Umbenennen</Button>
          </>
        }
      >
        <Input autoFocus aria-label="Neuer Name" value={umbenennen?.name ?? ""} onChange={(e) => setUmbenennen((u) => (u ? { ...u, name: e.target.value } : u))} />
      </Modal>

      <PageHeader
        title={pkg.name}
        zurueck={zurueck}
        subtitle={
          <>
            Änderungen werden sofort gespeichert.
            {pkg.archivedAt != null && <span className="ml-2 rounded-full bg-surface-container-high px-2 py-0.5 text-label-md dark:bg-white/[0.08]">archiviert</span>}
          </>
        }
        actions={
          <Button type="button" variant="secondary" disabled={busy} onClick={() => void duplicatePkg()}>
            <Icon name="content_copy" className="text-[18px]" /> Duplizieren
          </Button>
        }
      />

      {formError && <ErrorNote message={formError} />}

      <div className="space-y-4">
        <Card className="p-5">
          {/* Migration 242: Bei einer Pauschale wird der Pauschalbetrag gepflegt, nicht ein Preis je
              Gast. Die Beschriftung sagt, welcher gemeint ist; das Speichern waehlt die Spalte. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            <label className="block min-w-0">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">{art.pauschal ? "Pauschalpreis gesamt (€)" : "Preis pro Gast (€)"}</span>
              <Input
                ref={preisFeldRef}
                // W6 (BUGHUNT-2026-08-15): deutsche Anzeige ("45,00"), nicht toFixed-Punkt.
                defaultValue={centsToEuros(art.betragCents)}
                inputMode="decimal"
                onChange={(e) => setPreisLive(parseGermanEurosToCents(e.target.value))}
                onBlur={(e) => {
                  const cents = parseGermanEurosToCents(e.target.value);
                  if (cents == null) return setFormError("Ungültiger Betrag – z. B. 45,00.");
                  // Punkt R: unter den Getränkeanteil nicht speichern — die Aufteilung zeigt den Fehler.
                  if (pkg.drinksShareCents != null && cents < pkg.drinksShareCents) return;
                  const aktuell = paketPreisart(pkg);
                  // W6: PATCH nur bei ECHTER Aenderung. Ausnahme: der Pauschalbetrag liegt noch in
                  // der alten Personen-Spalte (Migration 242) — dann heilt der Schreibaufruf die Ablage.
                  if (cents === aktuell.betragCents && !(aktuell.pauschal && aktuell.betragAusPersonenSpalte)) return;
                  setPreisGeaendert(true);
                  void patchPkg(aktuell.pauschal ? { priceFlatCents: cents, pricePerPersonCents: 0 } : { pricePerPersonCents: cents });
                }}
              />
            </label>
            <label className="block min-w-0">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Name</span>
              <Input
                defaultValue={pkg.name}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== pkg.name) void patchPkg({ name: value });
                }}
              />
            </label>
            {/* Beschreibung - Kachel-Text + Angebotsvorschau. */}
            <label className="block min-w-0 sm:col-span-2">
              <span className="mb-1 block font-body-sm text-body-sm font-semibold">Beschreibung</span>
              <Input
                defaultValue={pkg.description ?? ""}
                placeholder="kurz, optional"
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value === (pkg.description ?? "")) return;
                  void patchPkg({ description: value || null });
                }}
              />
              <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                Erscheint auf der Paket-Kachel und in der Angebotsvorschau für den Gast.
              </span>
            </label>
            <PaketAufteilungFelder
              preisCents={preisLive ?? art.betragCents}
              gespeichertCents={pkg.drinksShareCents ?? null}
              preisGeaendert={preisGeaendert}
              onSpeichern={(drinksShareCents) => void patchPkg({ drinksShareCents })}
            />
          </div>
        </Card>

        <PaketWebsiteKarte paket={pkg} kategorien={kategorien} weinKandidaten={weinKandidaten} speichern={(patch) => patchPkg(patch)} />
        <PaketWebsiteVorschau paket={pkg} kategorien={kategorien} />

        {/* Migration 390: Menues INNERHALB dieses Pakets - genau eines ist aktiv und wird verwendet
            (z. B. vom KI-Auto-Entwurf). Die Positionen darunter gehoeren zum ausgewaehlten Menue. */}
        <Card className="p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className={sectionTitle}>
              Menüs in diesem Paket <span className="normal-case tracking-normal text-secondary/70">· {pkg.gastWaehltMenue ? "alle gehen ins Angebot, der Gast wählt" : "genau eines ist aktiv"}</span>
            </h2>
            <button type="button" className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary hover:underline" onClick={() => setAddMenuOpen((v) => !v)}>
              <Icon name="add_circle" className="text-[16px]" /> Neues Menü
            </button>
          </div>
          {addMenuOpen && (
            <form className="mb-3 flex flex-wrap gap-2" onSubmit={(e) => void addMenu(e)} data-testid="paket-menue-neu">
              <Input name="menuName" required className="min-w-0 flex-1" placeholder="z. B. Menü B · Vegetarisch" autoFocus />
              {pkg.gastWaehltMenue === true && (
                <>
                  <div className="w-[170px]">
                    <Select value={neuMenueArt} options={MENUE_ART_OPTIONEN} ariaLabel="Art des neuen Menüs" testId="paket-menue-neu-art" onValueChange={setNeuMenueArt} />
                  </div>
                  <div className="flex w-[150px] items-center gap-2">
                    <Input inputMode="decimal" aria-label="Aufpreis je Gast des neuen Menüs (optional)" placeholder="Aufpreis (optional)" value={neuMenueAufpreis} onChange={(ev) => setNeuMenueAufpreis(ev.target.value)} />
                    <span className="shrink-0 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">€ je Gast</span>
                  </div>
                </>
              )}
              <Button type="submit" variant="primary" className="!py-1.5">Anlegen</Button>
            </form>
          )}
          <div className="flex flex-wrap gap-2">
            {menus.map((m) => (
              <div
                key={m.id}
                data-testid="paket-menu-chip"
                data-menu-name={m.name}
                className={`flex min-w-0 items-center gap-2 rounded-lg border px-3 py-1.5 ${m.id === selectedMenuId ? "border-primary" : "border-outline-variant/40 dark:border-outline/40"}`}
              >
                <button
                  type="button"
                  className="min-w-0 break-words text-left font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface"
                  onClick={() => selectMenu(m.id)}
                  onDoubleClick={() => setUmbenennen({ id: m.id, name: m.name })}
                  title="Klick: Positionen anzeigen · Doppelklick: umbenennen"
                >
                  {m.name}
                </button>
                <button type="button" className="flex items-center text-secondary hover:text-primary dark:text-secondary-fixed-dim" title="Menü umbenennen" onClick={() => setUmbenennen({ id: m.id, name: m.name })}>
                  <Icon name="edit" className="text-[15px]" />
                </button>
                {m.active ? (
                  <span className="rounded-full bg-success/15 px-2 py-0.5 font-label-md text-[10px] font-semibold text-success">Aktiv</span>
                ) : (
                  <>
                    <button type="button" className="shrink-0 font-body-sm text-[11px] font-semibold text-primary hover:underline" onClick={() => void activateMenu(m.id)}>
                      Aktiv schalten
                    </button>
                    {menus.length > 1 && (
                      <button type="button" className="flex items-center text-secondary hover:text-error dark:text-secondary-fixed-dim" title="Menü löschen" onClick={() => void deleteMenu(m.id, m.name)}>
                        <Icon name="delete" className="text-[16px]" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </Card>

        <PaketMenuewahlKarte menus={menus} gastWaehlt={pkg.gastWaehltMenue === true} onSchalter={(an) => void schalteMenuewahl(an)} onMenue={(menuId, patch) => void patchMenue(menuId, patch)} meldung={menuewahlMeldung} />

        <Card className="p-5">
          <h2 className={`mb-3 ${sectionTitle}`}>Positionen — {menus.find((m) => m.id === selectedMenuId)?.name ?? ""}</h2>
          {/* M-47: Ladefehler sperrt das Editieren. */}
          {itemsLoadError ? (
            <div className="space-y-2 rounded-lg border border-error/40 bg-error/5 p-4">
              <p className="font-body-sm text-body-sm text-error">Positionen konnten nicht geladen werden: {itemsLoadError}</p>
              <Button type="button" variant="secondary" className="!py-1.5" onClick={() => loadPackage(selectedMenuId ?? undefined)}>Erneut laden</Button>
            </div>
          ) : !itemsLoaded ? (
            <p className="rounded-lg border border-dashed border-outline-variant/50 p-4 text-center font-body-sm text-body-sm text-secondary dark:border-outline/40 dark:text-secondary-fixed-dim">
              Positionen werden geladen …
            </p>
          ) : items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-outline-variant/50 p-4 text-center font-body-sm text-body-sm text-secondary dark:border-outline/40 dark:text-secondary-fixed-dim">
              Noch keine Positionen – fügen Sie unten eine hinzu.
            </p>
          ) : (
            <>
              {/* Spaltenkoepfe nur ab sm - auf Handybreite bricht jede Zeile in Name / Werte um. */}
              <div className={`mb-1.5 hidden gap-3 px-3 sm:flex ${sectionTitle} !text-[10.5px]`}>
                <span className="flex-1">Position</span>
                <span className="w-[104px] text-center">Menge / Gast</span>
                <span className="w-[108px] text-right">Preis</span>
                <span className="w-7" />
              </div>
              <ul className="space-y-1.5" data-testid="paket-positionen">
                {items.map((it) => (
                  <li key={it.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-outline-variant/30 px-3 py-2 dark:border-outline/30">
                    <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
                      <div className="flex items-start gap-1.5">
                        {/* Positionsname direkt editierbar - bei einer Katalog-Position eine
                            Ueberschreibung NUR fuer dieses Paket. */}
                        {/* Mehrzeilig statt abgeschnitten (Punkt 129): waechst mit dem Namen mit;
                            Enter speichert wie Blur, ein Zeilenumbruch gehoert nicht in den Namen. */}
                        <Textarea
                          rows={1}
                          ref={passeHoeheAn}
                          className="min-h-8 w-full min-w-0 flex-1 resize-none overflow-hidden !py-1 text-body-sm leading-snug"
                          defaultValue={it.name ?? ""}
                          onInput={(e) => passeHoeheAn(e.currentTarget)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={(e) => renameItem(it, e.target.value.replace(/\s*\n\s*/g, " "))}
                          maxLength={PAKET_POSITION_NAME_MAX}
                          aria-label="Positionsname"
                        />
                        <span className="mt-2 shrink-0 font-label-md text-[10px] uppercase tracking-wider text-secondary/70 dark:text-secondary-fixed-dim/70">
                          {it.source === "freetext" ? "Freitext" : "Katalog"}
                        </span>
                        {it.itemArchivedAt != null && (
                          <span className="mt-1.5 shrink-0 rounded bg-secondary/15 px-1.5 py-0.5 font-label-md text-[10px] font-semibold text-secondary dark:text-[#b8ae9e]" title="Diese Position wurde archiviert (z. B. Karte ersetzt) — Gang austauschen oder entfernen.">
                            archiviert
                          </span>
                        )}
                      </div>
                      {/* LMIV: Allergene/Diät je Gang direkt an der Position sichtbar. */}
                      <AllergenBadges allergens={it.allergens} checked={it.allergensCheckedAt != null} dietary={it.dietary} className="mt-0.5" />
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <Input
                        className="h-8 w-16 text-center text-body-sm"
                        title="Menge pro Gast"
                        defaultValue={(it.qtyPerGuestMilli / 1000).toString()}
                        inputMode="decimal"
                        onBlur={(e) => {
                          // W5 (BUGHUNT-2026-08-15): parseGermanDecimal statt `parseFloat(...) || 0` -
                          // ungueltig -> Formfehler + alten Wert behalten, kein Schreiben.
                          const menge = parseGermanDecimal(e.target.value);
                          if (menge == null) {
                            setFormError("Ungültige Menge – bitte als Zahl eingeben, z. B. 1,5.");
                            e.target.value = (it.qtyPerGuestMilli / 1000).toString();
                            return;
                          }
                          void saveItems(items.map((x) => (x.id === it.id ? { ...x, qtyPerGuestMilli: Math.round(menge * 1000) } : x)));
                        }}
                      />
                      <span className="w-10 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">/Gast</span>
                      <Input
                        className="h-8 w-24 text-right text-body-sm"
                        title="Preis (Überschreibung nur für dieses Paket)"
                        defaultValue={centsToEuros(it.priceCents ?? 0)}
                        inputMode="decimal"
                        onBlur={(e) => repriceItem(it, e.target.value)}
                      />
                      <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">€</span>
                      <button type="button" className="rounded p-1 text-error hover:bg-error/10" title="Entfernen" onClick={() => void saveItems(items.filter((x) => x.id !== it.id))}>
                        <Icon name="close" className="text-[16px]" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* EIN Feld mit Live-Autovorschlag aus dem GESAMTEN Katalog; kein Treffer -> Freitext. */}
          <div className="mt-3">
            {!addPosOpen ? (
              <button type="button" className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary hover:underline disabled:opacity-40" disabled={!itemsLoaded} onClick={() => setAddPosOpen(true)}>
                <Icon name="add_circle" className="text-[16px]" /> Position hinzufügen
              </button>
            ) : (
              <form className="flex flex-wrap items-start gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-high/40 p-3 dark:border-outline/40" onSubmit={(e) => void submitAddPosition(e)}>
                <div className="relative min-w-0 basis-full sm:basis-0 sm:flex-1">
                  <Input
                    placeholder="Bezeichnung tippen – Katalog-Treffer erscheinen automatisch …"
                    maxLength={PAKET_POSITION_NAME_MAX}
                    value={posName}
                    onChange={(e) => {
                      setPosName(e.target.value);
                      setPosMatchId(null);
                      setPosSuggestOpen(true);
                    }}
                    onFocus={() => setPosSuggestOpen(true)}
                    onBlur={() => setTimeout(() => setPosSuggestOpen(false), 150)}
                    autoFocus
                  />
                  {posSuggestOpen && posName.trim() !== "" && (
                    <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-auto rounded-lg border border-outline-variant/50 bg-surface shadow-lg dark:border-outline/50 dark:bg-surface-container-high">
                      {suggestions.length === 0 ? (
                        <p className="p-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">Kein Treffer im Katalog — „{posName.trim()}" bleibt als Freitext stehen</p>
                      ) : (
                        suggestions.map((ci) => (
                          <button
                            key={ci.id}
                            type="button"
                            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-surface-container-high/60 dark:hover:bg-surface-variant/20"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              pickSuggestion(ci);
                            }}
                          >
                            <span className="min-w-0 break-words font-body-sm text-body-sm font-medium">{ci.name}</span>
                            <span className="shrink-0 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{money(ci.priceCents)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <Input
                  className="w-28"
                  placeholder="Preis, z. B. 6,50"
                  inputMode="decimal"
                  value={posPrice}
                  onChange={(e) => setPosPrice(e.target.value)}
                  disabled={posMatchId != null}
                  title={posMatchId != null ? "Preis kommt aus dem Katalog" : "Preis für die freie Position"}
                />
                <Button type="submit" variant="primary" className="!py-1.5">Übernehmen</Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="!py-1.5"
                  onClick={() => {
                    setAddPosOpen(false);
                    setPosName("");
                    setPosPrice("");
                    setPosMatchId(null);
                  }}
                >
                  Abbrechen
                </Button>
              </form>
            )}
          </div>
        </Card>
      </div>

      <Modal
        open={preisRueckfrage != null}
        onClose={() => setPreisRueckfrage(null)}
        width="480px"
        title="Preis wirklich so ändern?"
        testId="preis-rueckfrage"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPreisRueckfrage(null);
                preisFeldRef.current?.focus();
              }}
            >
              Preis prüfen
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                const r = preisRueckfrage;
                setPreisRueckfrage(null);
                if (r) void patchPkg({ ...r.patch, confirmPriceChange: true });
              }}
            >
              Trotzdem speichern
            </Button>
          </>
        }
      >
        {preisRueckfrage && (
          <div className="space-y-3 font-body-sm text-body-sm">
            <p>
              Der Preis ändert sich von {money(preisRueckfrage.vonCents)} auf {money(preisRueckfrage.aufCents)}, das sind {preisRueckfrage.prozent} %{" "}
              {preisRueckfrage.aufCents < preisRueckfrage.vonCents ? "weniger" : "mehr"}. Das Paket ist auf der Website sichtbar. Die Website zeigt den neuen Preis
              innerhalb einer Minute.
            </p>
            <p className="flex items-start gap-2 rounded-lg bg-secondary/10 px-3 py-2 text-secondary dark:text-[#b8ae9e]">
              <Icon name="warning" className="text-[18px]" />
              <span>Vielleicht ein Tippfehler? Prüfe, ob das Komma richtig sitzt.</span>
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
